import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api/client';

export interface OrgMembership {
  member_id: number;
  org_id: number;
  org_name: string;
  org_slug: string;
  role: 'owner' | 'admin' | 'promoter' | 'staff';
  referral_code?: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  is_platform_admin: boolean;
  notification_channels: string;
  balance: number;
  orgs: OrgMembership[];
}

interface IdentifyResult {
  exists: boolean;
  has_password: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => !!user.value?.is_platform_admin);
  const orgs = computed(() => user.value?.orgs ?? []);

  function hasOrgRole(orgSlug: string, roles: string[]): boolean {
    if (user.value?.is_platform_admin) return true;
    return !!user.value?.orgs.some(o => o.org_slug === orgSlug && roles.includes(o.role));
  }

  async function identify(email: string): Promise<IdentifyResult> {
    return api.post<IdentifyResult>(
      '/auth/identify',
      { email },
      { skipAuth: true }
    );
  }

  async function requestMagicLink(email: string, name?: string, ref?: string): Promise<{ sent: boolean; created: boolean }> {
    return api.post<{ sent: boolean; created: boolean }>(
      '/auth/magic-link',
      { email, name, ref: ref || undefined },
      { skipAuth: true }
    );
  }

  async function fetchMe(): Promise<void> {
    try {
      const response = await api.get<{ user: User }>('/auth/me');
      if (response.user) {
        user.value = response.user;
      }
    } catch {
      // Non-critical — basic user info already set from token response
    }
  }

  async function verifyMagicLink(token: string): Promise<boolean> {
    loading.value = true;
    try {
      const response = await api.post<{ accessToken: string; user: User }>(
        '/auth/verify-magic',
        { token },
        { skipAuth: true }
      );
      api.setAccessToken(response.accessToken);
      user.value = { ...response.user, orgs: [] };
      fetchMe(); // fire-and-forget to populate org memberships
      return true;
    } catch {
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    loading.value = true;
    try {
      const response = await api.post<{ accessToken: string; user: User }>(
        '/auth/login',
        { email, password },
        { skipAuth: true }
      );
      api.setAccessToken(response.accessToken);
      user.value = { ...response.user, orgs: [] };
      fetchMe(); // fire-and-forget to populate org memberships
      return true;
    } catch {
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function refresh(): Promise<boolean> {
    try {
      const response = await api.post<{ accessToken: string; user: User }>(
        '/auth/refresh',
        null,
        { skipAuth: true }
      );
      api.setAccessToken(response.accessToken);
      user.value = { ...response.user, orgs: [] };
      fetchMe(); // fire-and-forget to populate org memberships
      return true;
    } catch {
      return false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors
    } finally {
      api.setAccessToken(null);
      user.value = null;
    }
  }

  async function updateNotificationPreferences(data: { channels?: string; telegram_chat_id?: string; whatsapp_number?: string }): Promise<boolean> {
    try {
      const res = await api.put<{ user: User }>('/account/notifications', data);
      if (user.value && res.user) {
        user.value.notification_channels = res.user.notification_channels;
      }
      return true;
    } catch {
      return false;
    }
  }

  function getRedirectPath(): string {
    if (!user.value) return '/';
    if (user.value.is_platform_admin) return '/admin';
    if (user.value.orgs.length === 1) return `/org/${user.value.orgs[0].org_slug}`;
    if (user.value.orgs.length > 1) return '/orgs';
    return '/account';
  }

  return {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    orgs,
    hasOrgRole,
    identify,
    requestMagicLink,
    fetchMe,
    verifyMagicLink,
    login,
    refresh,
    logout,
    updateNotificationPreferences,
    getRedirectPath,
  };
});
